import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { HeaderHeightContext } from 'expo-router/react-navigation';
import { useContext } from 'react';
import { Platform } from 'react-native';

import { SupportThreadView } from './SupportThreadView';
import { useSupportThreadHeaderProps } from './useSupportThreadHeaderProps';

import { ChatHeaderTitle, getChatHeaderItems } from '@/components/chat/ChatHeaderContent';
import { Header } from '@/components/header';
import { useLiquidGlass } from '@/components/ui/glass';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';

const IS_IOS = Platform.OS === 'ios';

/**
 * The support-thread route body on mobile layouts, titled with the other
 * party. Where iOS draws Liquid Glass the bar floats transparently over the
 * thread, whose scroll edge then blurs the messages beneath it, so the thread
 * pads itself clear of the bar. Everywhere else the bar is opaque in the
 * thread's own fill. The thread itself handles the safe area at the bottom,
 * since its composer floats there on glass.
 *
 * A thread opened from a link or a notification has nothing to go back to, so
 * the header's fallback back button returns to the inbox.
 */
export function SupportThreadScreen({ conversationId }: { conversationId: string }) {
  const { colors } = useColors();

  const hasGlass = useLiquidGlass();

  const headerHeight = useContext(HeaderHeightContext) ?? 0;

  const headerProps = useSupportThreadHeaderProps(conversationId);

  const isHeaderTransparent = IS_IOS && hasGlass;

  return (
    <VStack className="w-full flex-1 bg-background-50">
      <Header
        headerShadowVisible
        isBackButton
        backRoute={ROUTES.INBOX}
        contentScrollsUnderHeader
        statusBarBackgroundColor={colors.background[50]}
        headerStyle={{ backgroundColor: colors.background[50] }}
        {...(isWeb
          ? { headerTitle: () => <ChatHeaderTitle {...headerProps} /> }
          : { leftItems: getChatHeaderItems(headerProps) })}
      />

      <SupportThreadView
        conversationId={conversationId}
        topInset={isHeaderTransparent ? headerHeight : 0}
      />
    </VStack>
  );
}
